import {
    faBan,
    faCheck,
    faTrash,
    faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import type { IPagedListAction } from "../../components/UI/Declarations";

/** Verb labels for enable/disable/delete/restore buttons (list actions, footers, confirm dialogs). */
export const RecordLifecycleActionLabels = {
    disable: "Disable",
    enable: "Enable",
    delete: "Delete",
    restore: "Restore",
} as const;

export type RecordLifecycleConfirmKind = "disable" | "enable" | "softDelete" | "restore";

export function recordLifecycleConfirmTitle(
    kind: RecordLifecycleConfirmKind,
    entityLabel: string
): string {
    switch (kind) {
        case "disable":
            return `${RecordLifecycleActionLabels.disable} ${entityLabel}`;
        case "enable":
            return `${RecordLifecycleActionLabels.enable} ${entityLabel}`;
        case "softDelete":
            return `${RecordLifecycleActionLabels.delete} ${entityLabel}`;
        case "restore":
            return `${RecordLifecycleActionLabels.restore} ${entityLabel}`;
    }
}

export function recordLifecycleConfirmButtonText(kind: RecordLifecycleConfirmKind): string {
    switch (kind) {
        case "disable":
            return RecordLifecycleActionLabels.disable;
        case "enable":
            return RecordLifecycleActionLabels.enable;
        case "softDelete":
            return RecordLifecycleActionLabels.delete;
        case "restore":
            return RecordLifecycleActionLabels.restore;
    }
}

export function recordLifecycleConfirmBody(
    kind: RecordLifecycleConfirmKind,
    recordLabel: string
): string {
    switch (kind) {
        case "disable":
            return `${RecordLifecycleActionLabels.disable} "${recordLabel}"?`;
        case "enable":
            return `${RecordLifecycleActionLabels.enable} "${recordLabel}"?`;
        case "softDelete":
            return `Remove "${recordLabel}"?`;
        case "restore":
            return `${RecordLifecycleActionLabels.restore} "${recordLabel}"?`;
    }
}

export type RecordLifecycleConfirmProps = {
    title: string;
    body: string;
    buttonSuccessVariant: "warning" | "primary" | "danger";
    buttonSuccessText: string;
};

const confirmVariantByKind: Record<RecordLifecycleConfirmKind, RecordLifecycleConfirmProps["buttonSuccessVariant"]> = {
    disable: "warning",
    enable: "primary",
    softDelete: "danger",
    restore: "primary",
};

/** Standard list-page confirm copy (disable/enable/delete/restore). */
export function recordLifecycleListConfirmBodies(
    recordLabel: string
): Partial<Record<RecordLifecycleConfirmKind, string>> {
    return {
        disable: `${RecordLifecycleActionLabels.disable} "${recordLabel}"? It will not appear in dropdown lists until enabled again.`,
        enable: `${RecordLifecycleActionLabels.enable} "${recordLabel}" for use in lists again?`,
        softDelete: `Remove "${recordLabel}"? It will be hidden from lists until you restore it.`,
        restore: `${RecordLifecycleActionLabels.restore} "${recordLabel}"? It will reappear in lists (still inactive until you enable it).`,
    };
}

export function buildRecordLifecycleConfirmProps(
    kind: RecordLifecycleConfirmKind | null,
    entityLabel: string,
    recordLabel: string,
    bodyByKind?: Partial<Record<RecordLifecycleConfirmKind, string>>
): RecordLifecycleConfirmProps {
    if (!kind) {
        return {
            title: "Confirm",
            body: "",
            buttonSuccessVariant: "primary",
            buttonSuccessText: "Yes",
        };
    }

    return {
        title: recordLifecycleConfirmTitle(kind, entityLabel),
        body: bodyByKind?.[kind] ?? recordLifecycleConfirmBody(kind, recordLabel),
        buttonSuccessVariant: confirmVariantByKind[kind],
        buttonSuccessText: recordLifecycleConfirmButtonText(kind),
    };
}

/** List rows include lifecycle flags at runtime; T is not limited to only those keys. */
export function buildRecordLifecyclePagedListActions<T extends object>(
    openConfirm: (kind: RecordLifecycleConfirmKind, item: T) => void,
    options?: { activeMember?: "isEnabled" | "isActive" }
): IPagedListAction[] {
    const activeMember = options?.activeMember ?? "isEnabled";

    return [
        {
            text: RecordLifecycleActionLabels.disable,
            icon: faBan,
            itemClassName: "text-warning",
            onClick: (item: T) => openConfirm("disable", item),
            availableMember: activeMember,
            hideWhenMember: "isDeleted",
        },
        {
            text: RecordLifecycleActionLabels.enable,
            icon: faCheck,
            itemClassName: "text-warning",
            onClick: (item: T) => openConfirm("enable", item),
            availableMember: activeMember,
            availableFliped: true,
            hideWhenMember: "isDeleted",
        },
        {
            text: RecordLifecycleActionLabels.delete,
            icon: faTrash,
            itemClassName: "text-danger",
            onClick: (item: T) => openConfirm("softDelete", item),
            availableMember: "isDeleted",
            availableFliped: true,
        },
        {
            text: RecordLifecycleActionLabels.restore,
            icon: faWandMagicSparkles,
            itemClassName: "text-success",
            onClick: (item: T) => openConfirm("restore", item),
            availableMember: "isDeleted",
        },
    ];
}
